import React, { useState } from 'react';
import { signInWithEmailAndPassword, getIdToken } from "firebase/auth";
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import Card from './ui/Card';
import Input from './ui/Input';

const ULTRA_ADMIN_UID = 'ADA5WZOA5UNIai1WaE52S33DvX73';

function UltraAdminLoginScreen({ onLoginSuccess }) {
    const { user } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // If already logged in, check if it's the ultra admin
    React.useEffect(() => {
        if (user && user.uid === ULTRA_ADMIN_UID) {
            onLoginSuccess?.();
        } else if (user && user.uid !== ULTRA_ADMIN_UID) {
            setError('Access denied. You do not have ultra-admin privileges.');
        }
    }, [user, onLoginSuccess]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;

            // Check if this is the ultra admin UID
            if (firebaseUser.uid !== ULTRA_ADMIN_UID) {
                setError('Access denied. This account does not have ultra-admin privileges.');
                // Sign out the user since they don't have permission
                await auth.signOut();
                return;
            }

            console.log("Ultra Admin Login Successful:", firebaseUser.uid);
            onLoginSuccess?.();
        } catch (firebaseError) {
            console.error("Ultra Admin Login Error:", firebaseError);
            setError(firebaseError.message || 'Failed to login.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
            <Card className="w-full max-w-md">
                <div className="p-8">
                    <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Ultra Admin Access</h2>
                        <p className="text-sm text-gray-600">Restricted database management interface</p>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                                Email
                            </label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                                required
                            />
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
                                Password
                            </label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                                required
                                minLength={6}
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={isLoading}
                            variant="primary"
                        >
                            {isLoading ? 'Signing In...' : 'Access Ultra Admin'}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-xs text-gray-500">
                            This interface is restricted to authorized personnel only.
                        </p>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default UltraAdminLoginScreen; 